import { ServiceTechnology } from 'src/DeviceIntegrationManagement/Providers/DeviceIntegrationProvider';

/**
 * Fallback visual identity for systems without a logo: a monogram (initials)
 * on a deterministically colored tile — like GitHub org avatars. Deliberately
 * carries NO semantics about device types or capabilities; a system is a
 * product, not a device class. New systems get a stable color for free.
 */
export interface SystemFallbackVisual {
  initials: string;
  /** Base color for the tile (used at low alpha for the background). */
  color: string;
}

/** Initials from the system name: first letter of up to two words. */
const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');

/**
 * Deterministic hue from the system key (djb2-style string hash). Fixed
 * saturation/lightness keep every generated color in the same tonal family so
 * tiles look designed rather than random.
 */
const colorOf = (key: string): string => {
  let hash = 5381;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 33) ^ key.charCodeAt(i);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 45%, 42%)`;
};

export const getSystemFallbackVisual = (system: ServiceTechnology): SystemFallbackVisual => ({
  initials: initialsOf(system.name),
  color: colorOf(system.manufacturer?.name ?? system.name),
});
